import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { badgeIcon } from "@/lib/badges";

const COLORS = ["#004aad", "#f59e0b", "#ef4444", "#10b981", "#8b5cf6", "#ec4899", "#facc15"];

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 90 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1.2,
    duration: 2.4 + Math.random() * 1.8,
    size: 6 + Math.random() * 6,
    color: COLORS[i % COLORS.length],
    rotate: Math.random() * 360,
    round: Math.random() > 0.6,
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="badge-confetti">
      {pieces.map((p) => (
        <span key={p.id} className="badge-confetti-piece" style={{
          left: `${p.left}%`, width: p.size, height: p.round ? p.size : p.size * 0.5, background: p.color,
          borderRadius: p.round ? "50%" : 2, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`,
          transform: `rotate(${p.rotate}deg)`,
        }} />
      ))}
    </div>
  );
}

export default function BadgeCelebration() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [badges, setBadges] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (user?.role !== "student") return;
    Promise.all([api.get("/gamification/celebrate"), api.get("/gamification/catalog")])
      .then(([c, cat]) => {
        setCatalog(Object.fromEntries((cat.data || []).map((b) => [b.id, b])));
        setBadges(c.data?.badges || []);
      })
      .catch(() => {});
  }, [user?.role]);

  if (!badges.length) return null;
  const b = badges[idx];
  const def = catalog[b.badge_id] || {};
  const Icon = badgeIcon(def.icon);
  const L = (o) => o?.[lang] ?? o?.es ?? "";
  const total = badges.reduce((s, x) => s + (x.points || 0), 0);

  const close = async (goToAchievements = false) => {
    const ids = badges.map((x) => x.id);
    setBadges([]);
    try { await api.post("/gamification/celebrate/ack", { ids }); } catch { /* best effort */ }
    if (goToAchievements) navigate("/dashboard/achievements");
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md overflow-hidden text-center" data-testid="badge-celebration">
        <Confetti />
        <div className="relative pt-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">{t("celebration_title")}</p>
          <div className="mx-auto mt-4 h-24 w-24 rounded-full bg-yellow-100 border-4 border-yellow-400 flex items-center justify-center badge-pop shadow-lg">
            <Icon className="h-11 w-11 text-yellow-700" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-4" data-testid="badge-celebration-name">
            {L(def.name)}{(def.tiers || []).length > 1 ? ` · ${t("level")} ${b.tier}` : ""}
          </h2>
          <p className="text-sm text-slate-600 mt-1">{L(def.desc)}</p>
          {b.note && <p className="text-xs text-slate-500 mt-1 italic">“{b.note}”</p>}
          <p className="font-display text-3xl font-bold text-brand-blue mt-3" data-testid="badge-celebration-points">+{b.points} {t("points_short")}</p>
          {badges.length > 1 && (
            <p className="text-xs text-slate-500 mt-2" data-testid="badge-celebration-counter">
              {idx + 1} / {badges.length} · {t("celebration_total", { n: total })}
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {idx < badges.length - 1 ? (
              <Button onClick={() => setIdx((i) => i + 1)} data-testid="badge-celebration-next">{t("celebration_next")}</Button>
            ) : (
              <Button onClick={() => close(false)} data-testid="badge-celebration-close">{t("celebration_close")}</Button>
            )}
            <Button variant="outline" onClick={() => close(true)} data-testid="badge-celebration-view">{t("my_achievements")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
